import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import { confirmCoursePayment } from "../api/courses";
import logger from "../utils/logger.js";

export default function CoursePaymentSuccess() {
  const { lang } = useLanguage();
  const isAr = lang === "ar";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;

    async function confirm() {
      const sessionId = searchParams.get("session_id");
      const token = searchParams.get("token");
      const provider = (searchParams.get("provider") || (token && !sessionId ? "paypal" : "stripe")).toLowerCase();
      const mode = searchParams.get("mode") || "";

      if (!sessionId && !token) {
        setError(isAr ? "معرّف جلسة الدفع غير موجود." : "Payment session ID is missing.");
        setLoading(false);
        return;
      }

      try {
        const data = await confirmCoursePayment({ sessionId, token, provider, mode });
        if (!active) return;
        setResult(data);
      } catch (err) {
        logger.error("Course payment success error", err);
        if (!active) return;
        setError(err?.message || (isAr ? "تعذر تأكيد الدفع." : "Unable to confirm payment."));
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    confirm();

    return () => {
      active = false;
    };
  }, [searchParams, isAr]);

  return (
    <div className="container py-5" dir={isAr ? "rtl" : "ltr"}>
      <div className="row justify-content-center">
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4">
            <div className="card-body p-4 p-lg-5 text-center">
              {loading ? (
                <>
                  <div className="spinner-border text-primary mb-3" role="status" />
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "جارِ تأكيد الدفع..." : "Confirming payment..."}
                  </h1>
                </>
              ) : error ? (
                <>
                  <div className="display-6 mb-3">!</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تعذر تأكيد الدفع" : "Payment confirmation failed"}
                  </h1>
                  <p className="text-muted mb-4">{error}</p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <Link to="/courses" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "العودة للكورسات" : "Back to Courses"}
                    </Link>
                  </div>
                </>
              ) : (
                <>
                  <div className="display-6 mb-3">✓</div>
                  <h1 className="h3 fw-bold mb-2">
                    {isAr ? "تم الدفع بنجاح" : "Payment completed successfully"}
                  </h1>
                  <p className="text-muted mb-4">
                    {isAr
                      ? "تم تسجيلك في الكورس، ويمكنك البدء بالتعلم الآن."
                      : "You are now enrolled in the course and can start learning right away."}
                  </p>
                  <div className="d-flex flex-wrap justify-content-center gap-2">
                    <button
                      type="button"
                      className="btn btn-primary rounded-pill px-4"
                      onClick={() =>
                        navigate(`/learn/${result?.course_slug || result?.course_id}`)
                      }
                    >
                      {isAr ? "ابدأ التعلم" : "Start Learning"}
                    </button>
                    <Link to="/my-courses" className="btn btn-outline-dark rounded-pill px-4">
                      {isAr ? "كورساتي" : "My Courses"}
                    </Link>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
